import { PRIVILEGES } from 'constants/privileges';
import { SyntheticEvent, useState } from 'react';
import {
  Box,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tab,
  Tabs,
} from '@mui/material';
import { useAuth } from 'contexts/authContext';
import { useTranslation } from 'react-i18next';
import { hasPrivilege } from 'utils/authFunctions.utils';
import * as S from '../../styles';
import { DIALOGS } from '../../TransportCloudTable.consts';
import { DriversDatabaseTable } from './DriversDatabase/DriversDatabaseTable';
import { StatusesTable } from './Statuses/StatusesTable';

type TabKey = 'statuses' | 'drivers';

type Props = {
  open: boolean;
  onClose: () => void;
};

export const ManagementDialog = ({ open, onClose }: Props) => {
  const { t } = useTranslation(['misc']);
  const { user } = useAuth();

  const canManageStatuses = hasPrivilege(
    PRIVILEGES.TransportCloud.MANAGE_CONTAINER_STATUSES,
    user,
  );
  const canManageDrivers = hasPrivilege(
    PRIVILEGES.TransportCloud.MANAGE_DRIVERS_DATABASE,
    user,
  );

  const [activeTab, setActiveTab] = useState<TabKey>(
    canManageStatuses ? 'statuses' : 'drivers',
  );

  const handleChange = (_: SyntheticEvent, value: TabKey) =>
    setActiveTab(value);

  return (
    <S.ManagementStyledDialog open={open} onClose={onClose}>
      <DialogTitle>{DIALOGS.cloudManagement}</DialogTitle>
      <DialogContent dividers>
        <S.TabsBar>
          <Tabs value={activeTab} onChange={handleChange}>
            {canManageStatuses && (
              <Tab label={DIALOGS.cloudManagementStatuses} value='statuses' />
            )}
            {canManageDrivers && (
              <Tab
                label={DIALOGS.cloudManagementDriversDatabase}
                value='drivers'
              />
            )}
          </Tabs>
        </S.TabsBar>

        <S.TabPane>
          {activeTab === 'statuses' && canManageStatuses && (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                gap: 2,
                alignItems: 'start',
              }}
            >
              <StatusesTable kind='IMPORT' />
              <StatusesTable kind='EXPORT' />
            </Box>
          )}
          {activeTab === 'drivers' && canManageDrivers && (
            <DriversDatabaseTable />
          )}
        </S.TabPane>
      </DialogContent>
      <DialogActions>
        <S.TCStyledButton onClick={onClose}>
          {t('button.close', { ns: 'misc' })}
        </S.TCStyledButton>
      </DialogActions>
    </S.ManagementStyledDialog>
  );
};
